import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { InfoIcon } from '@saesa/ui-kit-front';
import { notificationBell, tickCircleExito } from 'assets/images';
import { RootState } from 'store';
import { CADENA_VACIA } from 'utils/constants';
import { formatTerDisplay } from 'utils/formatters/dateFormatter';
import { KmlCampo, parseKmlOutageFields } from 'utils/kmlOutageMapper';
import { formatearNumeros } from 'utils/utilitario';
import {
  TEST_ID_BOTON_NOTIFICAR,
  TEST_ID_KML_CHART,
  TEST_ID_KML_DISCLAIMER,
  TEST_ID_KML_FALLBACK,
  TEST_ID_KML_FOOTER,
  TEST_ID_KML_MOTIVO,
  TEST_ID_KML_TER_BOX,
  TEST_ID_KML_TER_SUSCRITO,
  TEST_ID_KML_TER_VACIO,
} from './outageDashboard.constants';
import { buildFooterTexto, buildTerLabels } from './outageDashboard.helpers';
import BotonCerrarDashboard from './BotonCerrarDashboard';
import ModalSuscripcionTer from './ModalSuscripcionTer';
import useStyles from './OutageDashboardStyles';

/**
 * Card del polígono cuando no hay respuesta de tracking y se muestra lo leído del KML.
 * @param camposKml filas título/texto de la descripción del polígono
 * @param numeroOrden identificador de la orden
 * @param notificacionTerActivo indica si se puede ofrecer la suscripción al TER
 * @param fechaActualizacion fecha de última actualización del mapa
 */
const OutageKmlFallback = ({
  camposKml = [],
  numeroOrden,
  notificacionTerActivo,
  fechaActualizacion = CADENA_VACIA,
  onCerrar = undefined,
}: {
  camposKml?: KmlCampo[] | null;
  numeroOrden: string;
  notificacionTerActivo: boolean;
  fechaActualizacion?: string;
  onCerrar?: () => void;
}) => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Estado que indica si el modal de suscripción TER está abierto.
   */
  const [modalSuscripcionAbierto, setModalSuscripcionAbierto] = useState(false);

  /**
   * Indica si el cliente ya está suscrito al TER.
   */
  const clienteSuscrito = useSelector((state: RootState) => state.desconexion.clienteSuscritoTer);

  /**
   * Correo de la suscripción TER exitosa.
   */
  const correoSuscripcion = useSelector((state: RootState) => state.desconexion.correoSuscripcionTer);

  /**
   * Campos de corte leídos de la descripción del polígono.
   */
  const kml = useMemo(() => parseKmlOutageFields(camposKml), [camposKml]);

  const terLabels = buildTerLabels(t);
  const terDisplay = formatTerDisplay(kml.terFecha, terLabels);
  const footerTexto = buildFooterTexto(fechaActualizacion, t);

  const mostrarNotificar = notificacionTerActivo && !clienteSuscrito;
  const mostrarBannerSuscrito = notificacionTerActivo && clienteSuscrito;
  const mostrarChart = kml.clientesSinServicio !== null;

  /**
   * Cierra el modal de suscripción cuando el cliente ya quedó suscrito.
   */
  useEffect(() => {
    if (clienteSuscrito) {
      setModalSuscripcionAbierto(false);
    }
  }, [clienteSuscrito]);

  /**
   * Abre el modal para suscribirse a cambios del TER.
   */
  const abrirNotificar = () => setModalSuscripcionAbierto(true);

  /**
   * Banner de confirmación cuando el cliente ya se suscribió al TER.
   * @returns bloque "Recibimos tu correo"
   */
  const renderBannerSuscrito = () => (
    <div className={styles.terBoxSuscrito} data-testid={TEST_ID_KML_TER_SUSCRITO}>
      <img src={tickCircleExito} alt={CADENA_VACIA} className={styles.terSuscritoCheck} aria-hidden />
      <div className={styles.terSuscritoTextos}>
        <p className={styles.terSuscritoTitulo}>{t('desconexion:paginas.mapaCortes.notificacionTer.clienteSuscrito.titulo')}</p>
        <p className={styles.terSuscritoText}>
          {t('desconexion:paginas.mapaCortes.notificacionTer.clienteSuscrito.subTitulo')}
          {correoSuscripcion ? <span className={styles.terSuscritoEmail}>{correoSuscripcion}</span> : null}
        </p>
      </div>
    </div>
  );

  /**
   * Arma la caja de reposición estimada, suscripción o TER en evaluación.
   * @returns contenido de la caja TER
   */
  const renderTerBox = () => {
    if (mostrarBannerSuscrito && !kml.tieneTer) {
      return renderBannerSuscrito();
    }

    if (!kml.tieneTer) {
      return (
        <div className={styles.terBox} data-testid={TEST_ID_KML_TER_VACIO}>
          <div className={styles.terTextoWrap}>
            <p className={styles.kmlTerLabel}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.reposicionEstimadaLabel')}</p>
            <p className={styles.terHora}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.enEvaluacion')}</p>
          </div>
          {mostrarNotificar && (
            <button type="button" className={styles.botonNotificar} onClick={abrirNotificar} data-testid={TEST_ID_BOTON_NOTIFICAR}>
              <img src={notificationBell} alt={CADENA_VACIA} className={styles.iconoNotificar} aria-hidden />
              {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.notificar')}
            </button>
          )}
        </div>
      );
    }

    if (mostrarBannerSuscrito) {
      return (
        <div className={styles.terBoxConSuscripcion} data-testid={TEST_ID_KML_TER_BOX}>
          <div className={styles.terTextoWrap}>
            <p className={styles.terHora}>{terDisplay.principal}</p>
            <p className={styles.terSub}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.esReposicion')}</p>
          </div>
          {renderBannerSuscrito()}
        </div>
      );
    }

    return (
      <div className={styles.terBox} data-testid={TEST_ID_KML_TER_BOX}>
        <div className={styles.terTextoWrap}>
          <p className={styles.terHora}>{terDisplay.principal}</p>
          <p className={styles.terSub}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.esReposicion')}</p>
        </div>
        {mostrarNotificar && (
          <button type="button" className={styles.botonNotificar} onClick={abrirNotificar} data-testid={TEST_ID_BOTON_NOTIFICAR}>
            <img src={notificationBell} alt={CADENA_VACIA} className={styles.iconoNotificar} aria-hidden />
            {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.ter.notificar')}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.root} ${styles.rootKml}`} data-testid={TEST_ID_KML_FALLBACK}>
      <div className={styles.scrollArea}>
        <div className={styles.stickyCabecera}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <span className={styles.statusDot} aria-hidden />
              <p className={styles.statusLabel}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.enCurso')}</p>
            </div>
          </div>
          {onCerrar ? <BotonCerrarDashboard onCerrar={onCerrar} /> : null}
        </div>

        <div className={`${styles.content} ${styles.contentKml}`}>
          <div className={`${styles.body} ${styles.kmlBody}`}>
            <div className={`${styles.leftCol} ${styles.kmlLeftCol} ${!mostrarChart ? styles.leftColFull : CADENA_VACIA}`}>
              <h2 className={styles.motivo} data-testid={TEST_ID_KML_MOTIVO}>
                {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.sinMotivoTitulo')}
              </h2>

              <div className={styles.kmlTerStack}>
                {renderTerBox()}
                <div className={styles.kmlDisclaimer} data-testid={TEST_ID_KML_DISCLAIMER}>
                  <InfoIcon className={styles.kmlDisclaimerIcon} />
                  <p className={styles.kmlDisclaimerText}>{t('desconexion:paginas.mapaCortes.modalTracking.infoSinTracking')}</p>
                </div>
              </div>
            </div>

            {mostrarChart && (
              <div className={styles.rightCol}>
                <div className={styles.chartCard} data-testid={TEST_ID_KML_CHART}>
                  <div className={styles.chartHeader}>
                    <p className={styles.chartHeaderText}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.chart.titulo')}</p>
                  </div>
                  <div className={`${styles.chartBody} ${styles.chartKmlBody}`}>
                    <div>
                      <p className={styles.sinServicioNum}>{formatearNumeros(kml.clientesSinServicio as number)}</p>
                      <div className={styles.sinServicioLabel}>
                        <span className={styles.dotRojo} />
                        <p className={styles.labelSecundario}>
                          {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.chart.sinServicio')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.footer} data-testid={TEST_ID_KML_FOOTER}>
        <p className={styles.footerText}>{footerTexto}</p>
      </div>

      {notificacionTerActivo && (
        <ModalSuscripcionTer open={modalSuscripcionAbierto} numeroCaso={numeroOrden} onClose={() => setModalSuscripcionAbierto(false)} />
      )}
    </div>
  );
};

OutageKmlFallback.defaultProps = {
  camposKml: [],
  fechaActualizacion: CADENA_VACIA,
  onCerrar: undefined,
};

export default OutageKmlFallback;
